import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Questions } from '../common-class/questions';
import { Answers } from '../common-class/answers';
import { SurveyMessages } from '../common-class/survey-messages';
import { Group, Matrix } from '../common-class/pages';

export interface SurveyAnswer {
  ValueID: string;
  AnswerText: string;
  Weight: string;
}

export interface SurveyQuestion {
  QNo: string;
  QuestionType: string;
  Title: string;
  Answers?: SurveyAnswer[];
  SubQuestionType?: string;
  Rows?: Array<{ ID: string; Label: string }>;
  Columns?: Array<{ ID: string; Label: string; Weight: string }>;
  group_no?: number;
}

export interface SurveyPage {
  PageNo: string;
  PageName: string;
  Questions: SurveyQuestion[];
  Groups?: Group[];
  Matrixs?: Matrix[];
}

export interface SurveySettings {
  SurveyHeader?: string;
  SurveyFooter?: string;
  isOverAllStyleAppliedToHeader?: boolean;
  isOverAllStyleAppliedToFooter?: boolean;
  DisplayQno?: boolean;
  surveyWidthType?: number; // 1: small, 2: medium, 3: full
  isMandatoryUpfront?: boolean;
}

export interface SurveyGroup {
  group_no: number;
  gtitle: string;
  PipeGTitle?: string;
  QuestionTip?: string;
  L_text?: string;
  H_text?: string;
  M_text?: string;
  M_Pos?: number;
  Matrix_id: number;
  colwidth?: number;
  isAnchorReverse?: boolean;
  ShowDemoSubQno?: boolean;
  answer_style?: number;
  isOverAllStyleApplied?: boolean;
  pipeTitle?: string;
  mgTextboxHeight?: number;
  IsScribbledSignature?: boolean;
  IsUploadImageSignature?: boolean;
  IsTypeSignature?: boolean;
}

export interface SurveyMatrix {
  matrix_id: number;
  Mtitle?: string;
  PipeMTitle?: string;
  questiontip?: string;
  QPos?: number;
  isMulti?: boolean;
  MultiColumnCount?: number;
  subq_col_width?: number;
  totalwidth?: number;
  VerticalSpaceCount?: number;
  IsSingleGrid?: boolean;
  isBranchEnabled?: boolean;
  SourceGrid?: number;
  SourcePosition?: number;
  SourceNumber?: number;
  TargetGrid?: string | number;
  isDisplayManage?: boolean;
  isOverAllStyleApplied?: boolean;
  isHiddenQ?: boolean;
}

export interface PreviewData {
  isManditorySkip: boolean;
  isByPassLogic: boolean;
  isShowHiddenQN: boolean;
  isPrevPgChnge: boolean;
  PrevPgNo: number;
  isPagePreview: boolean;
  isPreviewToggle: boolean;
  surveyFormat: number;
  isFromSB: boolean;
  viewport: string;
  ViewportToggle: number;
  PreviewPropChange: boolean;
}

export interface SurveyData {
  Error: string;
  SurveyTitle: string;
  WelcomeText: string;
  Pages: SurveyPage[];
  ThankYouPage: string;
  SurveyLanguage: string;
  SurveySettings?: SurveySettings;
  Groups?: Group[];
  Matrix?: Matrix[];
}

@Injectable({
  providedIn: 'root'
})
export class SurveyPreviewService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  // Survey messages instance
  private surveyMessages: SurveyMessages;

  // Preview data instance
  private previewData: PreviewData;

  constructor(private httpClient: HttpClient) {
    // Initialize with default messages
    this.surveyMessages = this.getDefaultSurveyMessages();
    // Initialize with default preview data
    this.previewData = this.getDefaultPreviewData();
  }

  /**
   * Fetch survey data from API endpoint
   * @param endpoint - API endpoint URL
   * @param corpno - Corporation number (optional)
   * @returns Observable of SurveyData
   */
  getSurveyFromAPI(endpoint: string, corpno?: string): Observable<SurveyData> {
    const url = endpoint.startsWith('http') ? endpoint : `${environment.navURL}${endpoint}`;
    
    // Add corpno as query parameter if provided
    let finalUrl = url;
    if (corpno && corpno.trim()) {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = `${url}${separator}corpno=${encodeURIComponent(corpno.trim())}`;
    }
    
    console.log('Making API request to:', finalUrl);
    
    return this.httpClient.get<SurveyData>(finalUrl, this.httpOptions).pipe(
      map(response => this.validateSurveyData(response)),
      catchError(error => {
        console.error('Error fetching survey from API:', error);
        return throwError(() => new Error(`Failed to fetch survey from API: ${error.message}`));
      })
    );
  }

  /**
   * Load survey data from a local JSON file
   * @param file - File object from file input
   * @returns Observable of SurveyData
   */
  loadSurveyFromFile(file: File): Observable<SurveyData> {
    return new Observable(observer => {
      if (!file) {
        observer.error(new Error('No file provided'));
        return;
      }

      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        observer.error(new Error('Please select a valid JSON file'));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (e: any) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          const validatedData = this.validateSurveyData(jsonData);
          observer.next(validatedData);
          observer.complete();
        } catch (error) {
          observer.error(new Error(`Invalid JSON format: ${error.message}`));
        }
      };

      reader.onerror = () => {
        observer.error(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  /**
   * Load survey data from a JSON string
   * @param jsonString - JSON string containing survey data
   * @returns Observable of SurveyData
   */
  loadSurveyFromJSONString(jsonString: string): Observable<SurveyData> {
    try {
      const jsonData = JSON.parse(jsonString);
      const validatedData = this.validateSurveyData(jsonData);
      return of(validatedData);
    } catch (error) {
      return throwError(() => new Error(`Invalid JSON format: ${error.message}`));
    }
  }

  /**
   * Load survey data from a file path (for development/testing)
   * @param filePath - Path to the JSON file
   * @returns Observable of SurveyData
   */
  loadSurveyFromFilePath(filePath: string): Observable<SurveyData> {
    // Handle assets folder paths
    let fullPath = filePath;
    if (filePath.startsWith('/assets/')) {
      fullPath = filePath.substring(1); // Remove leading slash for relative path
    } else if (filePath.startsWith('assets/')) {
      fullPath = filePath; // Already correct format
    } else {
      fullPath = 'assets/' + filePath; // Add assets prefix if missing
    }
    
    console.log('Loading survey from path:', fullPath);
    console.log('Full URL will be:', window.location.origin + '/' + fullPath);
    
    return this.httpClient.get<SurveyData>(fullPath).pipe(
      map(response => {
        console.log('Raw response from server:', response);
        const validatedData = this.validateSurveyData(response);
        console.log('Validated survey data:', validatedData);
        return validatedData;
      }),
      catchError(error => {
        console.error('Error loading survey from file path:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: error.url
        });
        return throwError(() => new Error(`Failed to load survey from file: ${error.message}`));
      })
    );
  }

  /**
   * Validate survey data structure
   * @param data - Raw survey data
   * @returns Validated SurveyData
   */
  private validateSurveyData(data: any): SurveyData {
    console.log('Validating survey data:', data);
    
    // Basic validation
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid survey data: data must be an object');
    }

    // Check if this is the new ai_survey1.json format with nested survey data
    if (data.survey && data.survey.Data && data.survey.Data.length > 0) {
      console.log('Detected new ai_survey1.json format, extracting survey data...');
      
      // Extract the survey data from the Response field
      const surveyResponse = data.survey.Data[0].Response;
      if (typeof surveyResponse === 'string') {
        try {
          const parsedSurveyData = JSON.parse(surveyResponse);
          console.log('Successfully parsed nested survey data:', parsedSurveyData);
          return this.validateSurveyData(parsedSurveyData); // Recursively validate the parsed data
        } catch (error) {
          console.error('Failed to parse nested survey data:', error);
          throw new Error('Invalid survey data: Failed to parse nested survey JSON');
        }
      } else if (typeof surveyResponse === 'object') {
        console.log('Survey data is already an object:', surveyResponse);
        return this.validateSurveyData(surveyResponse); // Recursively validate the object data
      }
    }

    // Standard validation for direct survey data
    if (!data.SurveyTitle || typeof data.SurveyTitle !== 'string') {
      throw new Error('Invalid survey data: SurveyTitle is required and must be a string');
    }

    if (!data.Pages || !Array.isArray(data.Pages) || data.Pages.length === 0) {
      throw new Error('Invalid survey data: Pages array is required and must contain at least one page');
    }

    // Validate each page
    data.Pages.forEach((page: any, pageIndex: number) => {
      if (!page.PageNo || !page.PageName || !Array.isArray(page.Questions)) {
        throw new Error(`Invalid page data at index ${pageIndex}: PageNo, PageName, and Questions array are required`);
      }
    });

    console.log('Survey data validation completed successfully');
    return data as SurveyData;
  }

  /**
   * Converts AI survey JSON data to Questions class format with default values
   */
  convertToQuestionsClass(aiSurveyData: any): Questions[] {
    const questions: Questions[] = [];
    let globalGroupCounter = 1; // Global counter for group numbers across entire survey
    
    if (aiSurveyData && aiSurveyData.Pages) {
      aiSurveyData.Pages.forEach((page: any, pageIndex: number) => {
        if (page && page.Questions) {
          page.Questions.forEach((question: any, qIndex: number) => {
                         // Check if this is a grid question with Rows and Columns (but not Multiple Text Box)
             if (this.isGridQuestion(question.QuestionType) && question.QuestionType !== 'Multiple Text Box' && question.Rows && question.Columns) {
              // For grid questions, create a question for each row
              question.Rows.forEach((row: any, rowIndex: number) => {
                const convertedQuestion = new Questions();
                
                // Map basic properties with defaults
                convertedQuestion.UniqueNumber = questions.length + 1;
                convertedQuestion.AnswerID = 0;
                convertedQuestion.selectAnsValue = 0;
                convertedQuestion.AnswerText = '';
                convertedQuestion.page_no = parseInt(page.PageNo) || pageIndex + 1;
                convertedQuestion.qno = parseInt(question.QNo) || qIndex + 1;
                convertedQuestion.uniqueqno = parseInt(question.QNo) || qIndex + 1;
                convertedQuestion.qtitle = row.Label || `Question ${row.ID}`; // Use row label as question title
                convertedQuestion.pipingQuationTitle = '';
                convertedQuestion.QuestionTip = '';
                convertedQuestion.qsubtype = this.getQuestionSubtype(question.QuestionType);
                convertedQuestion.qtype = this.getQuestionTypeCode(question.QuestionType);
                convertedQuestion.constraintType = 0;
                convertedQuestion.constraintNum = 0;
                
                // Other properties with defaults
                convertedQuestion.other = false;
                convertedQuestion.other_text = '';
                convertedQuestion.otherAnswer = '';
                convertedQuestion.isOtherAnswer = false;
                // Set text width for Text Box questions
                if (question.QuestionType === 'Text Box') {
                  convertedQuestion.twidth = 70;
                } else {
                  convertedQuestion.twidth = 0;
                }
                convertedQuestion.tHeight = 0;
                convertedQuestion.mandatory = false;
                convertedQuestion.isSoftRequired = false;
                convertedQuestion.isSoftRequiredHandle = false;
                convertedQuestion.SoftReqErrorMsg = '';
                convertedQuestion.qid = parseInt(question.QNo) || qIndex + 1;
                convertedQuestion.error_msg = '';
                convertedQuestion.hasImages = false;
                convertedQuestion.hasMedia = false;
                convertedQuestion.DispAnsCol = 1;
                convertedQuestion.DispAnsFormat = 0;
                convertedQuestion.totalAnswers = question.Columns ? question.Columns.length : 0;
                
                // Convert Columns to Answers for each row
                if (question.Columns && question.Columns.length > 0) {
                  convertedQuestion.Answers = question.Columns.map((column: any, colIndex: number) => {
                    const convertedAnswer = new Answers();
                    convertedAnswer.zarca_q_id = parseInt(question.QNo) || qIndex + 1;
                    convertedAnswer.value_id = parseInt(column.ID) || colIndex + 1;
                    convertedAnswer.value_text = column.Label || '';
                    convertedAnswer.ms_id = 0;
                    convertedAnswer.ParentMsId = 0;
                    convertedAnswer.IsNoneAbove = false;
                    convertedAnswer.weight = parseFloat(column.Weight) || 0;
                    convertedAnswer.isHide = false;
                    convertedAnswer.IsHideNew = false;
                    convertedAnswer.ControlAns = null;
                    convertedAnswer.selectdId = 0;
                    convertedAnswer.CQ_QID = 0;
                    convertedAnswer.Rule_id = 0;
                    convertedAnswer.pageto = 0;
                    convertedAnswer.AnswerImages = [];
                    convertedAnswer.AnswerMedias = [];
                    convertedAnswer.isOther = false;
                    convertedAnswer.hasImage = false;
                    convertedAnswer.hasMedia = false;
                    convertedAnswer.isValid = true;
                    convertedAnswer.isNA = column.Label === 'N/A' || column.Label === 'Not Applicable';
                    convertedAnswer.isQuotaDisable = false;
                    convertedAnswer.isQuotaHide = false;
                    convertedAnswer.QuotaMessage = '';
                    convertedAnswer.IsAnsPipe = false;
                    convertedAnswer.isADLAnswer = false;
                    convertedAnswer.isADLHide = false;
                    convertedAnswer.adlCondStatus = [];
                    
                    return convertedAnswer;
                  });
                } else {
                  convertedQuestion.Answers = [];
                }
                
                // Set remaining properties with defaults
                convertedQuestion.AnsPipeQID = 0;
                convertedQuestion.AnsPipeCondition = 0;
                convertedQuestion.AnsPipeRankLimit = 0;
                convertedQuestion.hasCorrectAns = false;
                convertedQuestion.isDrillSearchEnable = false;
                convertedQuestion.isShowReportingVal = false;
                
                // Set Rating Scale specific properties ONLY for rating scale questions
                if (question.QuestionType === 'Rating Smiley') {
                  convertedQuestion.RS_Type = 1;
                  convertedQuestion.RSFormat = 1;
                } else if (question.QuestionType === 'NPS') {
                  convertedQuestion.RS_Type = 2;
                  convertedQuestion.RSFormat = 3;
                } else if (question.QuestionType === 'Rating Slider') {
                  convertedQuestion.RS_Type = 0;
                  convertedQuestion.RSFormat = 0;
                }
                // For non-rating questions (like Text Box), don't set RS_Type and RSFormat
                
                convertedQuestion.isOverAllStyleApplied = false;
                convertedQuestion.MTBTitleLocation = 0;
                convertedQuestion.isreenter = 0;
                convertedQuestion.reEnterErrMsg = '';
                convertedQuestion.ImageSize = 0;
                convertedQuestion.isQuotaMgmtON = false;
                convertedQuestion.Rule_id = 0;
                convertedQuestion.DemographicFieldID = 0;
                convertedQuestion.SpssCode = '';
                convertedQuestion.isUniformWidth = false;
                convertedQuestion.isadvbranching = false;
                convertedQuestion.uniquecno = 0;
                convertedQuestion.apply_currency = 0;
                convertedQuestion.M_Text = 0;
                convertedQuestion.isAnchorReverse = false;
                convertedQuestion.append_percentage = 0;
                convertedQuestion.custom_format = '';
                convertedQuestion.currency_name = '';
                convertedQuestion.M_Pos = 0;
                convertedQuestion.other_pageto = 0;
                convertedQuestion.MediaText = '';
                convertedQuestion.rank_limit = 0;
                convertedQuestion.dna_pageto = 0;
                convertedQuestion.date_default = '';
                convertedQuestion.piping = false;
                convertedQuestion.answer_style = 0;
                convertedQuestion.branch = false;
                convertedQuestion.sub_qno = rowIndex; // Set sub-question number based on row index
                convertedQuestion.group_no = this.getGroupNumberForQuestion(question.QuestionType, qIndex, globalGroupCounter);
                // Only increment group counter once per grid question type, not per row
                if (convertedQuestion.group_no > 0 && rowIndex === 0) {
                  globalGroupCounter++; // Increment for next question type
                }
                convertedQuestion.tsize = '';
                convertedQuestion.ms_date = new Date();
                convertedQuestion.QuestionImageAndMedia_1 = null;
                convertedQuestion.QuestionImageAndMedia_2 = null;
                convertedQuestion.isSQ = false;
                convertedQuestion.isADLSQ = false;
                convertedQuestion.isCQ = false;
                convertedQuestion.isCQHide = false;
                convertedQuestion.rules = [];
                convertedQuestion.QDLCondStatuses = [];
                convertedQuestion.ADLRules = [];
                convertedQuestion.pageto = 0;
                convertedQuestion.population_type = 0;
                // Set character limit for Text Box questions
                if (question.QuestionType === 'Text Box') {
                  convertedQuestion.characterLimit = 100;
                  convertedQuestion.Showcharlimit = true;
                  convertedQuestion.RemainingChr = '100';
                } else {
                  convertedQuestion.characterLimit = 0;
                  convertedQuestion.Showcharlimit = false;
                  convertedQuestion.RemainingChr = '';
                }
                convertedQuestion.currency_Symbol = '';
                convertedQuestion.num_range_1 = '';
                convertedQuestion.num_range_2 = '';
                convertedQuestion.RotateIndex = 0;
                convertedQuestion.ansConstraint = '';
                convertedQuestion.dataSecretKey = '';
                convertedQuestion.subQnoinChar = '';
                convertedQuestion.uniform_width = false;
                convertedQuestion.IsShowClearOption = false;
                convertedQuestion.ClearOptionErrorMsg = '';
                convertedQuestion.AllowOnlyInteger = false;
                convertedQuestion.InlineQDL = false;
                convertedQuestion.isValid = false;
                convertedQuestion.isReEnterEmail = false;
                convertedQuestion.isReEnterEmailValid = false;
                convertedQuestion.selectedOptionList = [];
                convertedQuestion.isAlertDanger = false;
                convertedQuestion.isMultiQuestion = rowIndex > 0; // First row is main question, others are multi-questions
                convertedQuestion.isMatrixQuestion = this.isMatrixQuestion(question.QuestionType);
                convertedQuestion.isGroupRule = false;
                convertedQuestion.isNOGroupRule = false;
                convertedQuestion.isGroupCQHide = false;
                convertedQuestion.isMandatoryStar = false;
                convertedQuestion.isMultiControlValid = true;
                convertedQuestion.ReEnterBlankMessage = '';
                convertedQuestion.isNA = false;
                convertedQuestion.isLoading = false;
                convertedQuestion.IsDisabled = false;
                convertedQuestion.PrePopValue = 0;
                convertedQuestion.PrePopText = '';
                convertedQuestion.isMandatoryInvalid = false;
                convertedQuestion.isGoBackQuota = false;
                convertedQuestion.Custom_Message = '';
                convertedQuestion.DestinationQuestions = [];
                convertedQuestion.SelectedCountry = '';
                convertedQuestion.isFirstShowQuestion = false;
                convertedQuestion.isMultiQuestionPiping = false;
                convertedQuestion.IsOfflineSkipQuestion = false;
                convertedQuestion.IsShowEmoji = false;
                convertedQuestion.PaletteID = 0;
                convertedQuestion.IsADLRuleApplied = false;
                convertedQuestion.isAllADLHide = false;
                convertedQuestion.isInlineShown = false;
                convertedQuestion.isHiddenQ = false;
                convertedQuestion.grpRotateIndex = 0;
                convertedQuestion.reEnterEmailLabel = '';
                convertedQuestion.isPreviewPropChange = false;
                convertedQuestion.isDirectoryLinkedQ = false;
                convertedQuestion.isDirectoryHidden = false;
                
                               questions.push(convertedQuestion);
                 
                 // Debug logging for grid questions
                 if (rowIndex === 0) { // Only log once per grid question type
                   console.log('Grid question created:', {
                     questionType: question.QuestionType,
                     qno: convertedQuestion.qno,
                     group_no: convertedQuestion.group_no,
                     globalGroupCounter: globalGroupCounter,
                     groupNumberAssigned: convertedQuestion.group_no
                   });
                 }
               });
             } else if ((question.QuestionType === 'Multiple Text Box' || question.QuestionType === 'Attachment' || question.QuestionType === 'Signature') && question.Rows) {
               // Handle Multiple Text Box, Attachment, and Signature questions - create a single question with sub-questions
               const convertedQuestion = new Questions();
               
               // Map basic properties with defaults
               convertedQuestion.UniqueNumber = questions.length + 1;
               convertedQuestion.AnswerID = 0;
               convertedQuestion.selectAnsValue = 0;
               convertedQuestion.AnswerText = '';
               convertedQuestion.page_no = parseInt(page.PageNo) || pageIndex + 1;
               convertedQuestion.qno = parseInt(question.QNo) || qIndex + 1;
               convertedQuestion.uniqueqno = parseInt(question.QNo) || qIndex + 1;
               // For Multiple Text Box, Attachment, and Signature, the main question title should be the group title
               convertedQuestion.qtitle = question.Title || '';
               convertedQuestion.pipingQuationTitle = '';
               convertedQuestion.QuestionTip = '';
               convertedQuestion.qsubtype = this.getQuestionSubtype(question.QuestionType);
               convertedQuestion.qtype = this.getQuestionTypeCode(question.QuestionType);
               convertedQuestion.constraintType = 0;
               convertedQuestion.constraintNum = 0;
               
               // Set matrix question properties for Multiple Text Box
               convertedQuestion.isMatrixQuestion = true;
               convertedQuestion.isMultiQuestion = false; // Main question is not a multi-question
               
               // Other properties with defaults
               convertedQuestion.other = false;
               convertedQuestion.other_text = '';
               convertedQuestion.otherAnswer = '';
               convertedQuestion.isOtherAnswer = false;
               convertedQuestion.twidth = 0;
               convertedQuestion.tHeight = 0;
               convertedQuestion.mandatory = false;
               convertedQuestion.isSoftRequired = false;
               convertedQuestion.isSoftRequiredHandle = false;
               convertedQuestion.SoftReqErrorMsg = '';
               convertedQuestion.qid = parseInt(question.QNo) || qIndex + 1;
               convertedQuestion.error_msg = '';
               convertedQuestion.hasImages = false;
               convertedQuestion.hasMedia = false;
               convertedQuestion.DispAnsCol = 1;
               convertedQuestion.DispAnsFormat = 0;
               convertedQuestion.totalAnswers = question.Rows ? question.Rows.length : 0;
               
               // Convert Rows to Answers for Multiple Text Box
               if (question.Rows && question.Rows.length > 0) {
                 convertedQuestion.Answers = question.Rows.map((row: any, rowIndex: number) => {
                   const convertedAnswer = new Answers();
                   convertedAnswer.zarca_q_id = parseInt(question.QNo) || qIndex + 1;
                   convertedAnswer.value_id = parseInt(row.ID) || rowIndex + 1;
                   convertedAnswer.value_text = row.Label || '';
                   convertedAnswer.ms_id = 0;
                   convertedAnswer.ParentMsId = 0;
                   convertedAnswer.IsNoneAbove = false;
                   convertedAnswer.weight = 0;
                   convertedAnswer.isHide = false;
                   convertedAnswer.IsHideNew = false;
                   convertedAnswer.ControlAns = null;
                   convertedAnswer.selectdId = 0;
                   convertedAnswer.CQ_QID = 0;
                   convertedAnswer.Rule_id = 0;
                   convertedAnswer.pageto = 0;
                   convertedAnswer.AnswerImages = [];
                   convertedAnswer.AnswerMedias = [];
                   convertedAnswer.isOther = false;
                   convertedAnswer.hasImage = false;
                   convertedAnswer.hasMedia = false;
                   convertedAnswer.isValid = true;
                   convertedAnswer.isNA = false;
                   convertedAnswer.isQuotaDisable = false;
                   convertedAnswer.isQuotaHide = false;
                   convertedAnswer.QuotaMessage = '';
                   convertedAnswer.IsAnsPipe = false;
                   convertedAnswer.isADLAnswer = false;
                   convertedAnswer.isADLHide = false;
                   convertedAnswer.adlCondStatus = [];
                   
                   return convertedAnswer;
                 });
               } else {
                 convertedQuestion.Answers = [];
               }
               
               // Set remaining properties with defaults
               convertedQuestion.AnsPipeQID = 0;
               convertedQuestion.AnsPipeCondition = 0;
               convertedQuestion.AnsPipeRankLimit = 0;
               convertedQuestion.hasCorrectAns = false;
               convertedQuestion.isDrillSearchEnable = false;
               convertedQuestion.isShowReportingVal = false;
               convertedQuestion.isOverAllStyleApplied = false;
               convertedQuestion.MTBTitleLocation = 0;
               convertedQuestion.isreenter = 0;
               convertedQuestion.reEnterErrMsg = '';
               convertedQuestion.ImageSize = 0;
               convertedQuestion.isQuotaMgmtON = false;
               convertedQuestion.Rule_id = 0;
               convertedQuestion.DemographicFieldID = 0;
               convertedQuestion.SpssCode = '';
               convertedQuestion.isUniformWidth = false;
               convertedQuestion.isadvbranching = false;
               convertedQuestion.uniquecno = 0;
               convertedQuestion.apply_currency = 0;
               convertedQuestion.M_Text = 0;
               convertedQuestion.isAnchorReverse = false;
               convertedQuestion.append_percentage = 0;
               convertedQuestion.custom_format = '';
               convertedQuestion.currency_name = '';
               convertedQuestion.M_Pos = 0;
               convertedQuestion.other_pageto = 0;
               convertedQuestion.MediaText = '';
               convertedQuestion.rank_limit = 0;
               convertedQuestion.dna_pageto = 0;
               convertedQuestion.date_default = '';
               convertedQuestion.piping = false;
               convertedQuestion.answer_style = 0;
               convertedQuestion.branch = false;
               convertedQuestion.sub_qno = 0;
               convertedQuestion.group_no = this.getGroupNumberForQuestion(question.QuestionType, qIndex, globalGroupCounter);
               if (convertedQuestion.group_no > 0) {
                 globalGroupCounter++; // Increment for next question
               }
               convertedQuestion.tsize = '';
               convertedQuestion.ms_date = new Date();
               convertedQuestion.QuestionImageAndMedia_1 = null;
               convertedQuestion.QuestionImageAndMedia_2 = null;
               convertedQuestion.isSQ = false;
               convertedQuestion.isADLSQ = false;
               convertedQuestion.isCQ = false;
               convertedQuestion.isCQHide = false;
               convertedQuestion.rules = [];
               convertedQuestion.QDLCondStatuses = [];
               convertedQuestion.ADLRules = [];
               convertedQuestion.pageto = 0;
               convertedQuestion.population_type = 0;
               convertedQuestion.characterLimit = 0;
               convertedQuestion.Showcharlimit = false;
               convertedQuestion.RemainingChr = '';
               convertedQuestion.currency_Symbol = '';
               convertedQuestion.num_range_1 = '';
               convertedQuestion.num_range_2 = '';
               convertedQuestion.RotateIndex = 0;
               convertedQuestion.ansConstraint = '';
               convertedQuestion.dataSecretKey = '';
               convertedQuestion.subQnoinChar = '';
               convertedQuestion.uniform_width = false;
               convertedQuestion.IsShowClearOption = false;
               convertedQuestion.ClearOptionErrorMsg = '';
               convertedQuestion.AllowOnlyInteger = false;
               convertedQuestion.InlineQDL = false;
               convertedQuestion.isValid = false;
               convertedQuestion.isReEnterEmail = false;
               convertedQuestion.isReEnterEmailValid = false;
               convertedQuestion.selectedOptionList = [];
               convertedQuestion.isAlertDanger = false;
               convertedQuestion.isMultiQuestion = false;
               convertedQuestion.isMatrixQuestion = this.isMatrixQuestion(question.QuestionType);
               convertedQuestion.isGroupRule = false;
               convertedQuestion.isNOGroupRule = false;
               convertedQuestion.isGroupCQHide = false;
               convertedQuestion.isMandatoryStar = false;
               convertedQuestion.isMultiControlValid = true;
               convertedQuestion.ReEnterBlankMessage = '';
               convertedQuestion.isNA = false;
               convertedQuestion.isLoading = false;
               convertedQuestion.IsDisabled = false;
               convertedQuestion.PrePopValue = 0;
               convertedQuestion.PrePopText = '';
               convertedQuestion.isMandatoryInvalid = false;
               convertedQuestion.isGoBackQuota = false;
               convertedQuestion.Custom_Message = '';
               convertedQuestion.DestinationQuestions = [];
               convertedQuestion.SelectedCountry = '';
               convertedQuestion.isFirstShowQuestion = false;
               convertedQuestion.isMultiQuestionPiping = false;
               convertedQuestion.IsOfflineSkipQuestion = false;
               convertedQuestion.IsShowEmoji = false;
               convertedQuestion.PaletteID = 0;
               convertedQuestion.IsADLRuleApplied = false;
               convertedQuestion.isAllADLHide = false;
               convertedQuestion.isInlineShown = false;
               convertedQuestion.isHiddenQ = false;
               convertedQuestion.grpRotateIndex = 0;
               convertedQuestion.reEnterEmailLabel = '';
               convertedQuestion.isPreviewPropChange = false;
               convertedQuestion.isDirectoryLinkedQ = false;
               convertedQuestion.isDirectoryHidden = false;
               
               questions.push(convertedQuestion);
               
               // Now create sub-questions for each row (for Multiple Text Box, Attachment, and Signature)
               question.Rows.forEach((row: any, rowIndex: number) => {
                 const subQuestion = new Questions();
                 
                 // Explicitly copy essential properties from the main question
                 subQuestion.UniqueNumber = questions.length + 1;
                 subQuestion.AnswerID = convertedQuestion.AnswerID;
                 subQuestion.selectAnsValue = convertedQuestion.selectAnsValue;
                 subQuestion.AnswerText = convertedQuestion.AnswerText;
                 subQuestion.page_no = convertedQuestion.page_no;
                 subQuestion.qno = convertedQuestion.qno; // Same qno as main question
                 subQuestion.uniqueqno = convertedQuestion.uniqueqno;
                 subQuestion.pipingQuationTitle = convertedQuestion.pipingQuationTitle;
                 subQuestion.QuestionTip = convertedQuestion.QuestionTip;
                 subQuestion.qsubtype = convertedQuestion.qsubtype;
                 subQuestion.qtype = convertedQuestion.qtype;
                 subQuestion.constraintType = convertedQuestion.constraintType;
                 subQuestion.constraintNum = convertedQuestion.constraintNum;
                 subQuestion.other = convertedQuestion.other;
                 subQuestion.other_text = convertedQuestion.other_text;
                 subQuestion.otherAnswer = convertedQuestion.otherAnswer;
                 subQuestion.isOtherAnswer = convertedQuestion.isOtherAnswer;
                 subQuestion.twidth = convertedQuestion.twidth;
                 subQuestion.tHeight = convertedQuestion.tHeight;
                 subQuestion.mandatory = convertedQuestion.mandatory;
                 subQuestion.isSoftRequired = convertedQuestion.isSoftRequired;
                 subQuestion.isSoftRequiredHandle = convertedQuestion.isSoftRequiredHandle;
                 subQuestion.SoftReqErrorMsg = convertedQuestion.SoftReqErrorMsg;
                 subQuestion.qid = convertedQuestion.qid;
                 subQuestion.error_msg = convertedQuestion.error_msg;
                 subQuestion.hasImages = convertedQuestion.hasImages;
                 subQuestion.hasMedia = convertedQuestion.hasMedia;
                 subQuestion.DispAnsCol = convertedQuestion.DispAnsCol;
                 subQuestion.DispAnsFormat = convertedQuestion.DispAnsFormat;
                 subQuestion.totalAnswers = convertedQuestion.totalAnswers;
                 subQuestion.Answers = convertedQuestion.Answers;
                 subQuestion.AnsPipeQID = convertedQuestion.AnsPipeQID;
                 subQuestion.AnsPipeCondition = convertedQuestion.AnsPipeCondition;
                 subQuestion.AnsPipeRankLimit = convertedQuestion.AnsPipeRankLimit;
                 subQuestion.hasCorrectAns = convertedQuestion.hasCorrectAns;
                 subQuestion.isDrillSearchEnable = convertedQuestion.isDrillSearchEnable;
                 subQuestion.isShowReportingVal = convertedQuestion.isShowReportingVal;
                 subQuestion.isOverAllStyleApplied = convertedQuestion.isOverAllStyleApplied;
                 subQuestion.MTBTitleLocation = convertedQuestion.MTBTitleLocation;
                 subQuestion.isreenter = convertedQuestion.isreenter;
                 subQuestion.reEnterErrMsg = convertedQuestion.reEnterErrMsg;
                 subQuestion.ImageSize = convertedQuestion.ImageSize;
                 subQuestion.isQuotaMgmtON = convertedQuestion.isQuotaMgmtON;
                 subQuestion.Rule_id = convertedQuestion.Rule_id;
                 subQuestion.DemographicFieldID = convertedQuestion.DemographicFieldID;
                 subQuestion.SpssCode = convertedQuestion.SpssCode;
                 subQuestion.isUniformWidth = convertedQuestion.isUniformWidth;
                 subQuestion.isadvbranching = convertedQuestion.isadvbranching;
                 subQuestion.uniquecno = convertedQuestion.uniquecno;
                 subQuestion.apply_currency = convertedQuestion.apply_currency;
                 subQuestion.M_Text = convertedQuestion.M_Text;
                 subQuestion.isAnchorReverse = convertedQuestion.isAnchorReverse;
                 subQuestion.append_percentage = convertedQuestion.append_percentage;
                 subQuestion.custom_format = convertedQuestion.custom_format;
                 subQuestion.currency_name = convertedQuestion.currency_name;
                 subQuestion.M_Pos = convertedQuestion.M_Pos;
                 subQuestion.other_pageto = convertedQuestion.other_pageto;
                 subQuestion.MediaText = convertedQuestion.MediaText;
                 subQuestion.rank_limit = convertedQuestion.rank_limit;
                 subQuestion.dna_pageto = convertedQuestion.dna_pageto;
                 subQuestion.date_default = convertedQuestion.date_default;
                 subQuestion.piping = convertedQuestion.piping;
                 subQuestion.answer_style = convertedQuestion.answer_style;
                 subQuestion.branch = convertedQuestion.branch;
                 subQuestion.sub_qno = rowIndex + 1; // Sub-question number
                 subQuestion.group_no = convertedQuestion.group_no; // Same group_no as main question
                 subQuestion.tsize = convertedQuestion.tsize;
                 subQuestion.ms_date = convertedQuestion.ms_date;
                 subQuestion.QuestionImageAndMedia_1 = convertedQuestion.QuestionImageAndMedia_1;
                 subQuestion.QuestionImageAndMedia_2 = convertedQuestion.QuestionImageAndMedia_2;
                 subQuestion.isSQ = convertedQuestion.isSQ;
                 subQuestion.isADLSQ = convertedQuestion.isADLSQ;
                 subQuestion.isCQ = convertedQuestion.isCQ;
                 subQuestion.isCQHide = convertedQuestion.isCQHide;
                 subQuestion.rules = convertedQuestion.rules;
                 subQuestion.QDLCondStatuses = convertedQuestion.QDLCondStatuses;
                 subQuestion.ADLRules = convertedQuestion.ADLRules;
                 subQuestion.pageto = convertedQuestion.pageto;
                 subQuestion.population_type = convertedQuestion.population_type;
                 subQuestion.currency_Symbol = convertedQuestion.currency_Symbol;
                 subQuestion.num_range_1 = convertedQuestion.num_range_1;
                 subQuestion.num_range_2 = convertedQuestion.num_range_2;
                 subQuestion.RotateIndex = convertedQuestion.RotateIndex;
                 subQuestion.ansConstraint = convertedQuestion.ansConstraint;
                 subQuestion.dataSecretKey = convertedQuestion.dataSecretKey;
                 subQuestion.subQnoinChar = convertedQuestion.subQnoinChar;
                 subQuestion.uniform_width = convertedQuestion.uniform_width;
                 subQuestion.IsShowClearOption = convertedQuestion.IsShowClearOption;
                 subQuestion.ClearOptionErrorMsg = convertedQuestion.ClearOptionErrorMsg;
                 subQuestion.AllowOnlyInteger = convertedQuestion.AllowOnlyInteger;
                 subQuestion.InlineQDL = convertedQuestion.InlineQDL;
                 subQuestion.isValid = convertedQuestion.isValid;
                 subQuestion.isReEnterEmail = convertedQuestion.isReEnterEmail;
                 subQuestion.isReEnterEmailValid = convertedQuestion.isReEnterEmailValid;
                 subQuestion.selectedOptionList = convertedQuestion.selectedOptionList;
                 subQuestion.isAlertDanger = convertedQuestion.isAlertDanger;
                 subQuestion.isMatrixQuestion = convertedQuestion.isMatrixQuestion;
                 subQuestion.isGroupRule = convertedQuestion.isGroupRule;
                 subQuestion.isNOGroupRule = convertedQuestion.isNOGroupRule;
                 subQuestion.isGroupCQHide = convertedQuestion.isGroupCQHide;
                 subQuestion.isMandatoryStar = convertedQuestion.isMandatoryStar;
                 subQuestion.isMultiControlValid = convertedQuestion.isMultiControlValid;
                 subQuestion.ReEnterBlankMessage = convertedQuestion.ReEnterBlankMessage;
                 subQuestion.isNA = convertedQuestion.isNA;
                 subQuestion.isLoading = convertedQuestion.isLoading;
                 subQuestion.IsDisabled = convertedQuestion.IsDisabled;
                 subQuestion.PrePopValue = convertedQuestion.PrePopValue;
                 subQuestion.PrePopText = convertedQuestion.PrePopText;
                 subQuestion.isMandatoryInvalid = convertedQuestion.isMandatoryInvalid;
                 subQuestion.isGoBackQuota = convertedQuestion.isGoBackQuota;
                 subQuestion.Custom_Message = convertedQuestion.Custom_Message;
                 subQuestion.DestinationQuestions = convertedQuestion.DestinationQuestions;
                 subQuestion.SelectedCountry = convertedQuestion.SelectedCountry;
                 subQuestion.isFirstShowQuestion = convertedQuestion.isFirstShowQuestion;
                 subQuestion.isMultiQuestionPiping = convertedQuestion.isMultiQuestionPiping;
                 subQuestion.IsOfflineSkipQuestion = convertedQuestion.IsOfflineSkipQuestion;
                 subQuestion.IsShowEmoji = convertedQuestion.IsShowEmoji;
                 subQuestion.PaletteID = convertedQuestion.PaletteID;
                 subQuestion.IsADLRuleApplied = convertedQuestion.IsADLRuleApplied;
                 subQuestion.isAllADLHide = convertedQuestion.isAllADLHide;
                 subQuestion.isInlineShown = convertedQuestion.isInlineShown;
                 subQuestion.isHiddenQ = convertedQuestion.isHiddenQ;
                 subQuestion.grpRotateIndex = convertedQuestion.grpRotateIndex;
                 subQuestion.reEnterEmailLabel = convertedQuestion.reEnterEmailLabel;
                 subQuestion.isPreviewPropChange = convertedQuestion.isPreviewPropChange;
                 subQuestion.isDirectoryLinkedQ = convertedQuestion.isDirectoryLinkedQ;
                 subQuestion.isDirectoryHidden = convertedQuestion.isDirectoryHidden;
                 
                 // Set sub-question specific properties
                 // For sub-questions, use the row label as the title (for Multiple Text Box, Attachment, and Signature)
                 subQuestion.qtitle = row.Label || `Question ${row.ID}`;
                 subQuestion.isMultiQuestion = true; // Mark as sub-question
                 
                 // Set character limit for sub-questions (for Multiple Text Box, Attachment, and Signature)
                 subQuestion.characterLimit = 100;
                 subQuestion.Showcharlimit = true;
                 subQuestion.RemainingChr = '100';
                 
                 questions.push(subQuestion);
               });
               
               // Debug logging for Multiple Text Box, Attachment, and Signature questions
               console.log(`${question.QuestionType} created:`, {
                 mainQuestion: {
                   qno: convertedQuestion.qno,
                   group_no: convertedQuestion.group_no,
                   qtitle: convertedQuestion.qtitle,
                   sub_qno: convertedQuestion.sub_qno,
                   isMultiQuestion: convertedQuestion.isMultiQuestion,
                   isMatrixQuestion: convertedQuestion.isMatrixQuestion
                 },
                 subQuestionsCount: question.Rows ? question.Rows.length : 0,
                 totalQuestionsCreated: questions.length,
                 rows: question.Rows ? question.Rows.map(row => ({ id: row.ID, label: row.Label })) : [],
                 globalGroupCounter: globalGroupCounter,
                 groupNumberAssigned: convertedQuestion.group_no,
                 questionIndex: qIndex,
                 pageIndex: pageIndex
               });
             } else {
               // Handle regular questions (non-grid)
              const convertedQuestion = new Questions();
              
              // Map basic properties with defaults
              convertedQuestion.UniqueNumber = questions.length + 1;
              convertedQuestion.AnswerID = 0;
              convertedQuestion.selectAnsValue = 0;
              convertedQuestion.AnswerText = '';
              convertedQuestion.page_no = parseInt(page.PageNo) || pageIndex + 1;
              convertedQuestion.qno = parseInt(question.QNo) || qIndex + 1;
              convertedQuestion.uniqueqno = parseInt(question.QNo) || qIndex + 1;
              convertedQuestion.qtitle = question.Title || '';
              convertedQuestion.pipingQuationTitle = '';
              convertedQuestion.QuestionTip = '';
              convertedQuestion.qsubtype = this.getQuestionSubtype(question.QuestionType);
              convertedQuestion.qtype = this.getQuestionTypeCode(question.QuestionType);
              convertedQuestion.constraintType = 0;
              convertedQuestion.constraintNum = 0;
              
              // Other properties with defaults
              convertedQuestion.other = false;
              convertedQuestion.other_text = '';
              convertedQuestion.otherAnswer = '';
              convertedQuestion.isOtherAnswer = false;
              // Set text width for Text Box questions
              if (question.QuestionType === 'Text Box') {
                convertedQuestion.twidth = 70;
              } else {
                convertedQuestion.twidth = 0;
              }
              convertedQuestion.tHeight = 0;
              convertedQuestion.mandatory = false;
              convertedQuestion.isSoftRequired = false;
              convertedQuestion.isSoftRequiredHandle = false;
              convertedQuestion.SoftReqErrorMsg = '';
              convertedQuestion.qid = parseInt(question.QNo) || qIndex + 1;
              convertedQuestion.error_msg = '';
              convertedQuestion.hasImages = false;
              convertedQuestion.hasMedia = false;
              convertedQuestion.DispAnsCol = 1;
              convertedQuestion.DispAnsFormat = 0;
              convertedQuestion.totalAnswers = question.Answers ? question.Answers.length : 0;
              
              // Convert Answers
              if (question.QuestionType === 'NPS') {
                // For NPS questions, create standard NPS answers (0-10 + N/A)
                convertedQuestion.Answers = this.createNPSAnswers(parseInt(question.QNo) || qIndex + 1);
                convertedQuestion.totalAnswers = convertedQuestion.Answers.length;
              } else if (question.Answers && question.Answers.length > 0) {
                convertedQuestion.Answers = question.Answers.map((answer: any, aIndex: number) => {
                  const convertedAnswer = new Answers();
                  convertedAnswer.zarca_q_id = parseInt(question.QNo) || qIndex + 1;
                  convertedAnswer.value_id = parseInt(answer.ValueID) || aIndex + 1;
                  convertedAnswer.value_text = answer.AnswerText || '';
                  convertedAnswer.ms_id = 0;
                  convertedAnswer.ParentMsId = 0;
                  convertedAnswer.IsNoneAbove = false;
                  convertedAnswer.weight = parseFloat(answer.Weight) || 0;
                  convertedAnswer.isHide = false;
                  convertedAnswer.IsHideNew = false;
                  convertedAnswer.ControlAns = null;
                  convertedAnswer.selectdId = 0;
                  convertedAnswer.CQ_QID = 0;
                  convertedAnswer.Rule_id = 0;
                  convertedAnswer.pageto = 0;
                  convertedAnswer.AnswerImages = [];
                  convertedAnswer.AnswerMedias = [];
                  convertedAnswer.isOther = false;
                  convertedAnswer.hasImage = false;
                  convertedAnswer.hasMedia = false;
                  convertedAnswer.isValid = true;
                  convertedAnswer.isNA = false;
                  convertedAnswer.isQuotaDisable = false;
                  convertedAnswer.isQuotaHide = false;
                  convertedAnswer.QuotaMessage = '';
                  convertedAnswer.IsAnsPipe = false;
                  convertedAnswer.isADLAnswer = false;
                  convertedAnswer.isADLHide = false;
                  convertedAnswer.adlCondStatus = [];
                  
                  return convertedAnswer;
                });
              } else {
                convertedQuestion.Answers = [];
              }
              
              // Set remaining properties with defaults
              convertedQuestion.AnsPipeQID = 0;
              convertedQuestion.AnsPipeCondition = 0;
              convertedQuestion.AnsPipeRankLimit = 0;
              convertedQuestion.hasCorrectAns = false;
              convertedQuestion.isDrillSearchEnable = false;
              convertedQuestion.isShowReportingVal = false;
              
              // Set Rating Scale specific properties ONLY for rating scale questions
              if (question.QuestionType === 'Rating Smiley') {
                convertedQuestion.RS_Type = 1;
                convertedQuestion.RSFormat = 1;
              } else if (question.QuestionType === 'NPS') {
                convertedQuestion.RS_Type = 2;
                convertedQuestion.RSFormat = 3;
              } else if (question.QuestionType === 'Rating Slider') {
                convertedQuestion.RS_Type = 0;
                convertedQuestion.RSFormat = 0;
              }
              // For non-rating questions (like Text Box), don't set RS_Type and RSFormat
              
              convertedQuestion.isOverAllStyleApplied = false;
              convertedQuestion.MTBTitleLocation = 0;
              convertedQuestion.isreenter = 0;
              convertedQuestion.reEnterErrMsg = '';
              convertedQuestion.ImageSize = 0;
              convertedQuestion.isQuotaMgmtON = false;
              convertedQuestion.Rule_id = 0;
              convertedQuestion.DemographicFieldID = 0;
              convertedQuestion.SpssCode = '';
              convertedQuestion.isUniformWidth = false;
              convertedQuestion.isadvbranching = false;
              convertedQuestion.uniquecno = 0;
              convertedQuestion.apply_currency = 0;
              convertedQuestion.M_Text = 0;
              convertedQuestion.isAnchorReverse = false;
              convertedQuestion.append_percentage = 0;
              convertedQuestion.custom_format = '';
              convertedQuestion.currency_name = '';
              convertedQuestion.M_Pos = 0;
              convertedQuestion.other_pageto = 0;
              convertedQuestion.MediaText = '';
              convertedQuestion.rank_limit = 0;
              convertedQuestion.dna_pageto = 0;
              convertedQuestion.date_default = '';
              convertedQuestion.piping = false;
              convertedQuestion.answer_style = 0;
              convertedQuestion.branch = false;
              convertedQuestion.sub_qno = 0;
              convertedQuestion.group_no = this.getGroupNumberForQuestion(question.QuestionType, qIndex, globalGroupCounter);
              if (convertedQuestion.group_no > 0) {
                globalGroupCounter++; // Increment for next question
              }
              convertedQuestion.tsize = '';
              convertedQuestion.ms_date = new Date();
              convertedQuestion.QuestionImageAndMedia_1 = null;
              convertedQuestion.QuestionImageAndMedia_2 = null;
              convertedQuestion.isSQ = false;
              convertedQuestion.isADLSQ = false;
              convertedQuestion.isCQ = false;
              convertedQuestion.isCQHide = false;
              convertedQuestion.rules = [];
              convertedQuestion.QDLCondStatuses = [];
              convertedQuestion.ADLRules = [];
              convertedQuestion.pageto = 0;
              convertedQuestion.population_type = 0;
              // Set character limit for Text Box questions
              if (question.QuestionType === 'Text Box') {
                convertedQuestion.characterLimit = 100;
                convertedQuestion.Showcharlimit = true;
                convertedQuestion.RemainingChr = '100';
              } else {
                convertedQuestion.characterLimit = 0;
                convertedQuestion.Showcharlimit = false;
                convertedQuestion.RemainingChr = '';
              }
              convertedQuestion.currency_Symbol = '';
              convertedQuestion.num_range_1 = '';
              convertedQuestion.num_range_2 = '';
              convertedQuestion.RotateIndex = 0;
              convertedQuestion.ansConstraint = '';
              convertedQuestion.dataSecretKey = '';
              convertedQuestion.subQnoinChar = '';
              convertedQuestion.uniform_width = false;
              convertedQuestion.IsShowClearOption = false;
              convertedQuestion.ClearOptionErrorMsg = '';
              convertedQuestion.AllowOnlyInteger = false;
              convertedQuestion.InlineQDL = false;
              convertedQuestion.isValid = false;
              convertedQuestion.isReEnterEmail = false;
              convertedQuestion.isReEnterEmailValid = false;
              convertedQuestion.selectedOptionList = [];
              convertedQuestion.isAlertDanger = false;
              convertedQuestion.isMultiQuestion = false;
              convertedQuestion.isMatrixQuestion = this.isMatrixQuestion(question.QuestionType);
              convertedQuestion.isGroupRule = false;
              convertedQuestion.isNOGroupRule = false;
              convertedQuestion.isGroupCQHide = false;
              convertedQuestion.isMandatoryStar = false;
              convertedQuestion.isMultiControlValid = true;
              convertedQuestion.ReEnterBlankMessage = '';
              convertedQuestion.isNA = false;
              convertedQuestion.isLoading = false;
              convertedQuestion.IsDisabled = false;
              convertedQuestion.PrePopValue = 0;
              convertedQuestion.PrePopText = '';
              convertedQuestion.isMandatoryInvalid = false;
              convertedQuestion.isGoBackQuota = false;
              convertedQuestion.Custom_Message = '';
              convertedQuestion.DestinationQuestions = [];
              convertedQuestion.SelectedCountry = '';
              convertedQuestion.isFirstShowQuestion = false;
              convertedQuestion.isMultiQuestionPiping = false;
              convertedQuestion.IsOfflineSkipQuestion = false;
              convertedQuestion.IsShowEmoji = false;
              convertedQuestion.PaletteID = 0;
              convertedQuestion.IsADLRuleApplied = false;
              convertedQuestion.isAllADLHide = false;
              convertedQuestion.isInlineShown = false;
              convertedQuestion.isHiddenQ = false;
              convertedQuestion.grpRotateIndex = 0;
              convertedQuestion.reEnterEmailLabel = '';
              convertedQuestion.isPreviewPropChange = false;
              convertedQuestion.isDirectoryLinkedQ = false;
              convertedQuestion.isDirectoryHidden = false;
              
              questions.push(convertedQuestion);
              
              // Debug logging for questions with group numbers
              if (convertedQuestion.group_no > 0) {
                console.log('Question with group created:', {
                  questionType: question.QuestionType,
                  qno: convertedQuestion.qno,
                  group_no: convertedQuestion.group_no,
                  globalGroupCounter: globalGroupCounter
                });
              }
            }
          });
        }
      });
    }
    
    // Final summary of group numbers assigned
    console.log('=== FINAL GROUP NUMBER SUMMARY ===');
    const questionsWithGroups = questions.filter(q => q.group_no > 0);
    questionsWithGroups.forEach((q, index) => {
      console.log(`Question ${index + 1}: Type=${q.qtitle?.substring(0, 30)}..., Group=${q.group_no}, QNo=${q.qno}, SubQ=${q.sub_qno}`);
    });
    console.log('================================');
    
    return questions;
  }

  /**
   * Get group number for question based on type and increment global counter
   */
  private getGroupNumberForQuestion(questionType: string, questionIndex: number, globalGroupCounter: number): number {
    switch (questionType) {
      // Grid Questions need a proper group number
      case 'Grid':
      case 'Matrix Grid':
      case 'Rating Matrix':
      case 'Rating Grid Matrix':
      case 'Grid Questions':
      case 'Grid Checkbox':
      case 'Single Response Grid':
      case 'Single Choice Grid':
      case 'Rating Grid':
        return globalGroupCounter; // Return current value
        
      // Multiple Text Box questions need a group number
      case 'Multiple Text Box':
        return globalGroupCounter; // Return current value
        
      // Attachment and Signature questions need a group number
      case 'Attachment':
      case 'Signature':
        return globalGroupCounter; // Return current value
        
      // Other questions don't need groups
      default:
        return 0;
    }
  }

  /**
   * Create standard NPS answers (0-10 + N/A)
   */
  private createNPSAnswers(qid: number): Answers[] {
    const npsAnswers: Answers[] = [];
    
    console.log(`Creating NPS answers for question ID: ${qid}`);
    
    // Create answers for 0-10 scale
    for (let i = 0; i <= 10; i++) {
      const answer = new Answers();
      answer.zarca_q_id = qid;
      answer.value_id = i + 1; // value_id starts from 1
      if(i == 0){
        answer.value_text = 'Not at all Likely';
      }else if(i == 10){
        answer.value_text = 'Extremely Likely'; // Display the number
      }
      else{
        answer.value_text = '';
      }
      
      answer.ms_id = 0;
      answer.ParentMsId = 0;
      answer.IsNoneAbove = false;
      answer.weight = i; // Weight matches the scale value
      answer.isHide = false;
      answer.IsHideNew = false;
      answer.ControlAns = null;
      answer.selectdId = 0;
      answer.CQ_QID = 0;
      answer.Rule_id = 0;
      answer.pageto = 0;
      answer.AnswerImages = [];
      answer.AnswerMedias = [];
      answer.isOther = false;
      answer.hasImage = false;
      answer.hasMedia = false;
      answer.isValid = true;
      answer.isNA = false;
      answer.isQuotaDisable = false;
      answer.isQuotaHide = false;
      answer.QuotaMessage = '';
      answer.IsAnsPipe = false;
      answer.isADLAnswer = false;
      answer.isADLHide = false;
      answer.adlCondStatus = [];
      
      npsAnswers.push(answer);
      console.log(`Created NPS answer: value_id=${answer.value_id}, value_text="${answer.value_text}", weight=${answer.weight}`);
    }
    
    return npsAnswers;
  }

  /**
   * Determines question subtype based on question type
   */
  private getQuestionSubtype(questionType: string): number {
    switch (questionType) {
      // Text Questions (T)
      case 'Text Box':
        return 0; // General text box
      case 'Multiple Text Box':
        return 0; // Multiple text box (will be distinguished by group_no)
      case 'Attachment':
        return 71; // Attachment type
      case 'Signature':
        return 72; // Signature type
      
      // Radio Button Questions (R)
      case 'Radio Button':
        return 0; // Standard radio button
      case 'Horizontal Radio Button':
        return 0; // HR type
      
      // Check Box Questions (C)
      case 'Check Box':
      case 'Checkbox':
        return 0; // Standard checkbox
      
      // Drop Down Questions (L)
      case 'Drop Down':
      case 'Drop-down':
        return 0; // Standard dropdown
      
      // Rating Scale Questions (RS)
      case 'Rating Smiley':
        return 0; // RS type
      case 'NPS':
        return 0; // RS type
      case 'Rating Scale':
        return 0; // RS type
      case 'Rating Slider':
        return 0; // RS type
      case 'Rating Radio Button':
        return 7; // RT type with qsubtype 7
      
      // Grid Questions (GR, SR, GC, SC, RG)
      case 'Grid':
        return 0; // GR type
      
      // Matrix Questions (RW)
      case 'Matrix Grid':
        return 1; // RW type with qsubtype 1
      
      // Symbol Rating Scale (GQ)
      case 'Symbol Rating Scale':
        return 1; // GQ type with qsubtype 1 (Star)
      
      // Ranking Questions (RK)
      case 'Ranking':
        return 0; // RK type
      
      // Numeric Allocation (CS)
      case 'Numeric Allocation':
        return 0; // CS type
      
      // Demographics (GD)
      case 'Demographics':
        return 0; // GD type
      
      // Comment/Descriptive (CM)
      case 'Comment':
        return 0; // CM type
      case 'Descriptive Text':
        return 0; // CM type
      
      // Multiple Text Box
      case 'Multiple Text Box':
        return 0; // T type in matrix
      
      // Multiple Drop Down
      case 'Multiple Drop Down':
        return 0; // ML type
      
      // Drill Down
      case 'Drill Down':
        return 3; // RW type with qsubtype 3
      
      // Thumbs Up
      case 'Thumbs Up':
        return 2; // GQ type with qsubtype 2 (Thumbs up)
      
      // Question Spacer
      case 'Question Spacer':
        return 0;
      
      // Image Control
      case 'Image Control':
        return 0;
      
      // Media Control
      case 'Media Control':
        return 0;
      
      // Audio Player
      case 'Audio Player':
        return 0;
      
      // Media Player
      case 'Media Player':
        return 0;
      
      // Question Image and Media
      case 'Question Image and Media':
        return 0;
      
      // Question Title
      case 'Question Title':
        return 0;
      
      // Anonymous
      case 'Anonymous':
        return 0;
      
      // GDPR
      case 'GDPR':
        return 0;
      
      // Sign Up
      case 'Sign Up':
        return 0;
      
      // Survey Messages
      case 'Survey Messages':
        return 0;
      
      // Thank You Message
      case 'Thank You Message':
        return 0;
      
      // Offline
      case 'Offline':
        return 0;
      
      // Progress Bar
      case 'Progress Bar':
        return 0;
      
      // Header
      case 'Header':
        return 0;
      
      // Footer
      case 'Footer':
        return 0;
      
      // Answer Images
      case 'Answer Images':
        return 0;
      
      // Answer Media
      case 'Answer Media':
        return 0;
      
      // Telephone
      case 'Telephone':
        return 61; // T type with qsubtype 61 (Phone number)
      
      // Survey Messages
      case 'Survey Messages':
        return 0;
      
      // Short Key
      case 'Short Key':
        return 0;
      
      // Survey Decode
      case 'Survey Decode':
        return 0;
      
      // Survey Redirect
      case 'Survey Redirect':
        return 0;
      
      // Social Authentication
      case 'Social Authentication':
        return 0;
      
      // Edit Response
      case 'Edit Response':
        return 0;
      
      // Print Response
      case 'Print Response':
        return 0;
      
      // Survey Preview
      case 'Survey Preview':
        return 0;
      
      // Default fallback
      default:
        return 0;
    }
  }

  /**
   * Determines if question is a matrix question
   */
  private isMatrixQuestion(questionType: string): boolean {
    const matrixTypes = [
      'Grid',
      'Matrix Grid',
      'Multiple Text Box',
      'Multiple Drop Down',
      'Drill Down',
      'Grid Questions',
      'Matrix Questions',
      'Attachment',
      'Signature'
    ];
    
    return matrixTypes.includes(questionType);
  }

  /**
   * Determines if question is a grid question
   */
  private isGridQuestion(questionType: string): boolean {
    const gridTypes = [
      'Grid',
      'Matrix Grid',
      'Rating Matrix',
      'Rating Grid Matrix',
      'Grid Questions',
      'Grid Checkbox',
      'Single Response Grid',
      'Single Choice Grid',
      'Rating Grid'
    ];
    return gridTypes.includes(questionType);
  }

  /**
   * Determines question type code based on question type
   */
  private getQuestionTypeCode(questionType: string): string {
    switch (questionType) {
      // Text Questions (T)
      case 'Text Box':
        return 'T';
      case 'Multiple Text Box':
        return 'T';
      case 'Attachment':
        return 'T';
      case 'Signature':
        return 'T';
      
      // Radio Button Questions (R)
      case 'Radio Button':
        return 'R';
      case 'Horizontal Radio Button':
        return 'HR';
      
      // Check Box Questions (C)
      case 'Check Box':
      case 'Checkbox':
        return 'C';
      
      // Drop Down Questions (L)
      case 'Drop Down':
      case 'Drop-down':
        return 'L';
      
      // Rating Scale Questions (RS)
      case 'Rating Smiley':
        return 'RS';
      case 'NPS':
        return 'RS';
      case 'Rating Scale':
        return 'RS';
      case 'Rating Slider':
        return 'RS';
      case 'Rating Radio Button':
        return 'RT';
      
      // Grid Questions (GR, SR, GC, SC, RG)
      case 'Grid':
        return 'GR';
      
      // Matrix Questions (RW)
      case 'Matrix Grid':
        return 'RW';
      
      // Symbol Rating Scale (GQ)
      case 'Symbol Rating Scale':
        return 'GQ';
      
      // Ranking Questions (RK)
      case 'Ranking':
        return 'RK';
      
      // Numeric Allocation (CS)
      case 'Numeric Allocation':
        return 'CS';
      
      // Demographics (GD)
      case 'Demographics':
        return 'GD';
      
      // Comment/Descriptive (CM)
      case 'Comment':
        return 'CM';
      case 'Descriptive Text':
        return 'CM';
      
      // Multiple Text Box
      case 'Multiple Text Box':
        return 'T';
      
      // Multiple Drop Down
      case 'Multiple Drop Down':
        return 'ML';
      
      // Drill Down
      case 'Drill Down':
        return 'RW';
      
      // Thumbs Up
      case 'Thumbs Up':
        return 'GQ';
      
      // Question Spacer
      case 'Question Spacer':
        return 'QS';
      
      // Image Control
      case 'Image Control':
        return 'IC';
      
      // Media Control
      case 'Media Control':
        return 'MC';
      
      // Audio Player
      case 'Audio Player':
        return 'AP';
      
      // Media Player
      case 'Media Player':
        return 'MP';
      
      // Question Image and Media
      case 'Question Image and Media':
        return 'QIM';
      
      // Question Title
      case 'Question Title':
        return 'QT';
      
      // Anonymous
      case 'Anonymous':
        return 'AN';
      
      // GDPR
      case 'GDPR':
        return 'GDPR';
      
      // Sign Up
      case 'Sign Up':
        return 'SU';
      
      // Survey Messages
      case 'Survey Messages':
        return 'SM';
      
      // Thank You Message
      case 'Thank You Message':
        return 'TYM';
      
      // Offline
      case 'Offline':
        return 'OFF';
      
      // Progress Bar
      case 'Progress Bar':
        return 'PB';
      
      // Header
      case 'Header':
        return 'H';
      
      // Footer
      case 'Footer':
        return 'F';
      
      // Answer Images
      case 'Answer Images':
        return 'AI';
      
      // Answer Media
      case 'Answer Media':
        return 'AM';
      
      // Telephone
      case 'Telephone':
        return 'T';
      
      // Survey Messages
      case 'Survey Messages':
        return 'SM';
      
      // Short Key
      case 'Short Key':
        return 'SK';
      
      // Survey Decode
      case 'Survey Decode':
        return 'SD';
      
      // Survey Redirect
      case 'Survey Redirect':
        return 'SR';
      
      // Social Authentication
      case 'Social Authentication':
        return 'SA';
      
      // Edit Response
      case 'Edit Response':
        return 'ER';
      
      // Print Response
      case 'Print Response':
        return 'PR';
      
      // Survey Preview
      case 'Survey Preview':
        return 'SP';
      
      // Default fallback
      default:
        return 'T';
    }
  }

  /**
   * Get default survey data for fallback
   * @returns Default SurveyData
   */
  getDefaultSurveyData(): SurveyData {
    return {
      "Error": "NULL",
      "SurveyTitle": "Sample Survey",
      "WelcomeText": "Welcome to the sample survey. This is a default survey that appears when no data is loaded.",
      "Pages": [
        {
          "PageNo": "1",
          "PageName": "Sample Page",
          "Questions": [
            {
              "QNo": "1",
              "QuestionType": "Text Box",
              "Title": "Please enter your response:",
              "Answers": [
                {
                  "ValueID": "1",
                  "AnswerText": "",
                  "Weight": "NULL"
                }
              ]
            }
          ]
        }
      ],
      "ThankYouPage": "Thank you for completing the survey.",
      "SurveyLanguage": "English"
    };
  }

  /**
   * Get the survey messages instance
   * @returns SurveyMessages
   */
  getSurveyMessages(): SurveyMessages {
    return this.surveyMessages;
  }

  /**
   * Update survey messages with new data
   * @param messages - Partial survey messages to update
   */
  updateSurveyMessages(messages: Partial<SurveyMessages>): void {
    this.surveyMessages = { ...this.surveyMessages, ...messages };
  }

  /**
   * Get a specific message by key
   * @param key - Message key
   * @returns Message value or null
   */
  getSurveyMessage(key: keyof SurveyMessages): string | string[] | null {
    return this.surveyMessages[key] || null;
  }

  /**
   * Get default survey messages
   * @returns Default SurveyMessages
   */
  private getDefaultSurveyMessages(): SurveyMessages {
    return {
      NextBtnText: null,
      next: "Next",
      back: "Back",
      submit: "Submit",
      Continue1: "Continue",
      SaveContinueLater: "Save & continue later",
      ClearAllAnswers: "Clear answers on page",
      AsteriskMandatory: "Questions with an asterisk (<span class='icon__mandatory'></span>) are mandatory. Please answer all mandatory questions to proceed.",
      SurveyTimeOutMessage: "As a security measure, your survey timeout is set to 60 minutes of inactivity. If you leave this survey inactive for this duration, all your previous answers may be lost.",
      page: "Page",
      of1: "of",
      requiredInfo: "Required Information",
      ResponseRecorded: "Your response has been recorded.",
      AnonymousHdr: " Anonymous Survey",
      MobileAnonymousHdr: " Anonymous Survey",
      StrSaveHelpHeader: "Save & continue later",
      StrSaveHelp: "<p class=\"no-margin\">The feature \"Save & continue later\" allows respondents to save partially completed surveys and return later.</p> \r\n<p class=\"no-margin\">Respondents can access the saved survey by clicking the shared link from the save and continue later page to either</p><p class=\"no-margin\"> 1) Review answers already entered or</p> \r\n<p class=\"no-margin\">2) Continue taking the survey from the last session.</p>",
      RemainingChr: "Characters Remaining",
      ReEnterBlankMessage: "Please re-enter the email address.",
      ThankyouPage: null,
      PrintBlankSurvey: "Print Blank Survey",
      SurveyTimeOutWarning1: "It appears that you have been on this page for an extended period with no activity. If you require additional time, please click \"OK\". Failure to do so will result in loss of your responses.",
      SurveyTimeOutWarning2: "For security reasons your online survey session has been timed out due to inactivity. You can click on the participation link again to complete this survey.",
      accessedTestMode: "You have accessed this Survey in TEST MODE. Your responses will not be recorded in reports.",
      Rank: "Rank",
      DropDown: "--Select--",
      AnonymousMessage: "[{\"header\":\"Default Message\",\"desc\":\"Your responses are absolutely anonymous to the sender of this survey.\"},{\"header\":\"What does that mean?\",\"desc\":\"The sender will see your answers, but they won't be able to identify them with you personally. \"},{\"header\":\"How is that possible?\",\"desc\":\"The platform enables data collection while blocking the administrator from connecting your response with your email address, password, or IP address.\"},{\"header\":\"I received a reminder! So…?\",\"desc\":\"Nobody knows that you receive a reminder unless you tell them! In an anonymous survey, even the sender doesn't know who receives reminders.\"},{\"header\":\"Why this anonymity feature?\",\"desc\":\"We know that participants want to share honest answers but can sometimes feel that telling the whole truth is too risky. We also want to make sure that administrators can collect useful data and protect your privacy. The result? Accurate and useful reporting data and complete anonymity for you.\"}]",
      AnonymousMessageMobile: "<div style=\"padding: 14px;\">This is a Sogolytics Anonymous Survey.<br>Sogolytics's innovative technology makes it possible for you to participate in this survey and not have your responses associate with your email address or login.<br>For more information regarding Sogolytics's anonymous surveys, <click_here><br><a href=https://sogolyticsintuc.sevenpv.com/static/anonymouspage.aspx?lang=0&type=Sogolytics&isPoweredByClient=True&isAssessment=False target='_blank'>click here.</a></click_here></div>",
      AnonymousMessageHeader: " Anonymous Survey",
      Message: "Message",
      QuotaExaustMsg: "Quota limits for all answer options have been met. No response to this question can be submitted at this time. Please continue to the next page.",
      ParticipationQuotaExaust: "Your previous answer selected for this question has met its required quota. Please select another answer option.",
      AreUsureClear: "Are you sure you want to clear all the answers on this page? ",
      TestWarningMsg: "You have participated in a test version of this project. Your responses will not be included in reports.",
      TestHeader: "TEST MODE",
      TestGotIt: "Got it​",
      strOk: "OK",
      strWarning: "Warning:",
      Search: "Search",
      strinvalidvaluemsg: "Invalid Value! Please enter number.",
      Clear: "Clear",
      strCookieDisable: " Dear Survey Participant,<br /> <br />\r\n               It appears that your current browser settings are preventing you from viewing this Survey. <br />\r\n               <br /> Please try these steps to make this Survey available:\r\n                  <ul>\r\n                     <li> From the Settings menu, select Internet Options </li>\r\n                     <li> On the Security Page, set the Security Level to Medium </li>\r\n                     <li> On the Privacy Page, set the Privacy Level to Low. </li>\r\n                  </ul>\r\n                  For further assistance, contact your administrator. \r\n    ",
      strMonthArray: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      Total: "Total",
      strHiddenAnswerPipingText: "Answer(s) to this question are hidden due to answer piping settings.</ br > Please satisfy the conditions on previous pages to view the complete question.",
      AcceptCookieHeader: "Cookie Confirmation",
      AcceptCookieMessage: "Cookies are a required tool to ensure smooth participation. To review cookie permissions, see <MP>",
      AcceptCookieButton: "Accept All Cookies",
      strManagePreference: "Manage Preference.",
      strChooseCookies: "Please choose which cookies to allow.",
      strFuctionalCookies: "Functional Cookies",
      strAlwaysActive: "Always Active",
      strConsentSmoothParticipation: "These cookies are critical to ensure smooth participation.",
      strConsentChooseLanguage: "Choose the appropriate participation language",
      strConsentBrowserSupport: "Checks if your browser supports cookies",
      strConsentMaintainSession: "Maintains participation session",
      strConsentTracksStatus: "Tracks your participation status",
      strConsentRestrictedParticipation: "Restricts repeated participation",
      strConsentDownloadCookie: "Shows that the survey download has started",
      strConsentCookie: "Remembers that you consented to cookies",
      strAnalyticsCookies: "Analytics Cookies",
      strAnalyticsCookiesGoogle: " We use these cookies to track participation in Google Analytics.",
      strConsentGoogleAnalytics: "Tracks your participation in Google Analytics",
      strAcceptCookieChoices: "I accept these choices",
      strCookieCancle: "Cancel",
      CurrencyNegativeValueMessage: "Currency cannot be negative.",
      ReenterTextMulti: "Re-enter email address",
      Signature_SaveConfirm: "Save to Confirm",
      Signature_Clear: "Clear",
      Signature_ClickSave: "Click Save to Confirm",
      Signature_FileSizeExeedMessage: "The file to upload cannot exceed 5 MB.",
      Signature_FileTypeMessage: "Please upload a .jpg,.jpeg or .png file.",
      Signature_TapSignHere: "Tap to sign here.",
      Signature_ClickSignHere: "Click to sign here.",
      Signature_UploadSignature: "Upload Signature",
      Signature_TypeHere: "Type Here",
      Participation_Nomatchfound: "No match found.",
      Participation_ChooseLanguage: "Choose Language",
      RS_TypeValue: "Enter rating​",
      Title: null
    };
  }

  /**
   * Get default preview data
   * @returns Default PreviewData
   */
  private getDefaultPreviewData(): PreviewData {
    return {
      isManditorySkip: false,
      isByPassLogic: false,
      isShowHiddenQN: true,
      isPrevPgChnge: false,
      PrevPgNo: 0,
      isPagePreview: true,
      isPreviewToggle: true,
      surveyFormat: 0,
      isFromSB: false,
      viewport: 'web',
      ViewportToggle: 0,
      PreviewPropChange: false
    };
  }

  /**
   * Get current preview data
   * @returns PreviewData
   */
  getPreviewData(): PreviewData {
    return this.previewData;
  }

  /**
   * Update preview data
   * @param data - Partial PreviewData to update
   */
  updatePreviewData(data: Partial<PreviewData>): void {
    this.previewData = { ...this.previewData, ...data };
  }

  /**
   * Get a specific preview data property
   * @param key - Preview data key
   * @returns Preview data value or null
   */
  getPreviewDataProperty(key: keyof PreviewData): any {
    return this.previewData[key] || null;
  }
}
